import { useQueryClient, type MutationFunction } from "@tanstack/react-query";
import type { Currency } from "@/lib/domain/Currency";
import type { PriceSource } from "@/lib/domain/PriceSource";
import { paginatedUpdate, useHTTP, useHTTPMutation, type HTTPResponse } from "@/lib/http";
import type { PriceAlertDetailsModel } from "@/features/priceAlerts/api/models/PriceAlertDetailsModel";
import type { PriceAlertMetric } from "@/features/priceAlerts/PriceAlertMetric";
import type { PriceAlertStep } from "@/features/priceAlerts/PriceAlertStep";
import type { PriceAlertTarget } from "@/features/priceAlerts/PriceAlertTarget";

export interface PriceAlertUpdateRequestModel {
	readonly enabled: boolean;
	readonly targetType: PriceAlertTarget;
	readonly targetId: string;
	readonly metricType: PriceAlertMetric;
	readonly metric: number;
	readonly currency: Currency;
	readonly step: PriceAlertStep | null;
	readonly priceSource: PriceSource;
}

export interface PriceAlertUpdateRequestProperties {
	readonly priceAlertId: string;
	readonly requestModel: PriceAlertUpdateRequestModel;
}

export const usePriceAlertUpdate = () => {
	const http = useHTTP("Required");
	const queryClient = useQueryClient();

	const mutationFn: MutationFunction<HTTPResponse<PriceAlertDetailsModel>, PriceAlertUpdateRequestProperties> = requestProperties => {
		return http<PriceAlertDetailsModel, PriceAlertUpdateRequestModel>({
			method: "PUT",
			url: `/price-alert/${requestProperties.priceAlertId}`,
			body: requestProperties.requestModel
		});
	};

	return useHTTPMutation({
		mutationFn,
		onSuccess: data => {
			queryClient.setQueryData(["priceAlertDetails"], paginatedUpdate(data.body, x => x.priceAlertId === data.body.priceAlertId));
		}
	});
};
